/**
 * RunProgress — watch the agent build (or edit) the website, live.
 *
 * Left: the phase timeline (what's done, what's happening, what's next).
 * Right: the activity feed — the agent's own narration plus one line per
 * file it writes and check it runs — the same view we get in Claude Desktop,
 * translated to customer words. A credits meter ticks as the run spends.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  AlertTriangle,
  Check,
  CircleDashed,
  Coins,
  FileText,
  Hammer,
  Loader2,
  Rocket,
  Sparkles,
  StopCircle,
  TerminalSquare,
  Wand2,
} from 'lucide-react';
import { studioHelpers, type StudioEvent, type StudioPhase, type StudioRun } from '@/lib/api';
import { apiErrorMessage } from '@/lib/conflict';
import { Button } from '@/components/ui/button';
import { useAgentRunStream } from '@/hooks/useAgentRunStream';

const STEPS: Array<{ phase: StudioPhase; label: string; icon: typeof Sparkles }> = [
  { phase: 'understanding', label: 'Understanding your business', icon: Sparkles },
  { phase: 'designing', label: 'Designing the experience', icon: Wand2 },
  { phase: 'building', label: 'Building pages', icon: Hammer },
  { phase: 'verifying', label: 'Checking quality', icon: Check },
  { phase: 'publishing', label: 'Saving your website', icon: FileText },
  { phase: 'deploying', label: 'Going live', icon: Rocket },
];
const ORDER: StudioPhase[] = ['queued', 'provisioning', ...STEPS.map((s) => s.phase), 'done'];
const TERMINAL = new Set<StudioPhase>(['done', 'failed', 'cancelled', 'needs_credits']);

function phaseIndex(p: StudioPhase): number {
  const i = ORDER.indexOf(p);
  return i === -1 ? ORDER.length : i;
}

function describe(
  e: StudioEvent
): { icon: typeof FileText; text: string; tone: 'muted' | 'normal' | 'ok' | 'bad' } | null {
  const d = e.data ?? {};
  switch (e.type) {
    case 'assistant.text':
      return { icon: Sparkles, text: String(d.text ?? ''), tone: 'normal' };
    case 'file.written':
      return {
        icon: FileText,
        text: `${d.op === 'create' ? 'Created' : 'Updated'} ${String(d.label ?? d.path ?? 'a file')}`,
        tone: 'muted',
      };
    case 'tool.start': {
      const label = String(d.label ?? '');
      if (/^(Creating|Updating|Writing) /.test(label)) return null; // file.written covers it
      return { icon: TerminalSquare, text: label || 'Working', tone: 'muted' };
    }
    case 'build.start':
      return { icon: Hammer, text: 'Checking that everything builds…', tone: 'muted' };
    case 'build.ok':
      return { icon: Check, text: 'Build passed', tone: 'ok' };
    case 'build.fail':
      return { icon: AlertTriangle, text: 'Build failed — fixing it', tone: 'bad' };
    case 'commit':
      return {
        icon: FileText,
        text: `Saved your website (${String(d.files ?? '?')} files)`,
        tone: 'ok',
      };
    case 'deploy.started':
      return { icon: Rocket, text: 'Publishing to your address…', tone: 'muted' };
    case 'deploy.ok':
      return { icon: Rocket, text: 'Your website is live', tone: 'ok' };
    case 'deploy.failed':
      return {
        icon: AlertTriangle,
        text: `Publishing failed: ${String(d.error ?? 'unknown error')}`,
        tone: 'bad',
      };
    case 'run.failed':
      return { icon: AlertTriangle, text: String(d.message ?? 'The run failed'), tone: 'bad' };
    case 'run.cancelled':
      return { icon: StopCircle, text: 'Stopped', tone: 'bad' };
    case 'run.needs_credits':
      return { icon: Coins, text: 'Paused — more AI credits needed to continue', tone: 'bad' };
    case 'run.completed':
      return { icon: Check, text: 'All done', tone: 'ok' };
    case 'log':
      return d.level === 'info'
        ? { icon: CircleDashed, text: String(d.message ?? ''), tone: 'muted' }
        : null;
    default:
      return null;
  }
}

export function RunProgress({
  projectId,
  runId,
  initialRun,
  onFinished,
  onTopUp,
}: {
  projectId: string;
  runId: string;
  initialRun?: StudioRun | null;
  onFinished?: (run: StudioRun) => void;
  onTopUp?: () => void;
}) {
  const { events, run: liveRun, connected, usedCredits } = useAgentRunStream(projectId, runId);
  const run = liveRun ?? initialRun ?? null;
  const phase = run?.phase ?? 'queued';
  const feedRef = useRef<HTMLDivElement>(null);
  const [autoScroll, setAutoScroll] = useState(true);
  const finishedRef = useRef(false);

  useEffect(() => {
    if (run && TERMINAL.has(run.phase) && !finishedRef.current) {
      finishedRef.current = true;
      onFinished?.(run);
    }
  }, [run, onFinished]);

  useEffect(() => {
    if (autoScroll && feedRef.current) feedRef.current.scrollTop = feedRef.current.scrollHeight;
  }, [events, autoScroll]);

  const cancel = useMutation({
    mutationFn: () => studioHelpers.cancelRun(projectId, runId),
    onSuccess: () => toast.info('Stopping…'),
    onError: (err) => toast.error(apiErrorMessage(err, 'Could not stop the run')),
  });

  const feed = useMemo(
    () =>
      events
        .map((e) => ({ e, d: describe(e) }))
        .filter((x): x is { e: StudioEvent; d: NonNullable<ReturnType<typeof describe>> } =>
          Boolean(x.d)
        ),
    [events]
  );
  const files = useMemo(() => events.filter((e) => e.type === 'file.written').length, [events]);
  const credits = Math.max(usedCredits, run?.credits.used ?? 0, run?.credits.charged ?? 0);
  const failed = phase === 'failed' || phase === 'cancelled';
  const current = phaseIndex(phase);

  return (
    <div className="grid gap-6 lg:grid-cols-[280px_1fr]">
      {/* Timeline */}
      <div className="bg-card rounded-xl border p-5">
        <div className="mb-4 flex items-center justify-between">
          <p className="font-semibold">
            {run?.kind === 'edit' ? 'Applying your change' : 'Building your website'}
          </p>
          {!TERMINAL.has(phase) && <Loader2 className="text-primary h-4 w-4 animate-spin" />}
        </div>
        <ol className="space-y-3">
          {STEPS.map((s) => {
            const idx = phaseIndex(s.phase);
            const state: 'done' | 'active' | 'todo' =
              phase === 'done'
                ? 'done'
                : idx < current
                  ? 'done'
                  : idx === current
                    ? 'active'
                    : 'todo';
            const Icon = s.icon;
            return (
              <li key={s.phase} className="flex items-center gap-3">
                <span
                  className={`flex h-7 w-7 items-center justify-center rounded-full border ${
                    state === 'done'
                      ? 'border-emerald-500 bg-emerald-500 text-white'
                      : state === 'active'
                        ? failed
                          ? 'border-red-400 text-red-500'
                          : 'border-primary text-primary'
                        : 'text-muted-foreground'
                  }`}
                >
                  {state === 'done' ? (
                    <Check className="h-3.5 w-3.5" />
                  ) : state === 'active' && !failed ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Icon className="h-3.5 w-3.5" />
                  )}
                </span>
                <span
                  className={
                    state === 'todo' ? 'text-muted-foreground text-sm' : 'text-sm font-medium'
                  }
                >
                  {s.label}
                </span>
              </li>
            );
          })}
        </ol>
        <div className="mt-5 space-y-2 border-t pt-4 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground flex items-center gap-1.5">
              <Coins className="h-3.5 w-3.5" /> Credits used
            </span>
            <span className="font-semibold tabular-nums">{credits.toLocaleString()}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Files written</span>
            <span className="tabular-nums">{Math.max(files, run?.filesTouched ?? 0)}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Progress</span>
            <span className="tabular-nums">{run?.progress ?? 0}%</span>
          </div>
        </div>
        <div className="bg-muted mt-3 h-1.5 overflow-hidden rounded-full">
          <div
            className={`h-full transition-all ${failed ? 'bg-red-400' : 'bg-primary'}`}
            style={{ width: `${run?.progress ?? 2}%` }}
          />
        </div>
        {!TERMINAL.has(phase) && phase !== 'deploying' && (
          <Button
            variant="outline"
            size="sm"
            className="mt-4 w-full"
            disabled={cancel.isPending || run?.cancelRequested}
            onClick={() => cancel.mutate()}
          >
            <StopCircle className="mr-1.5 h-4 w-4" />
            {run?.cancelRequested ? 'Stopping…' : 'Stop'}
          </Button>
        )}
        {phase === 'needs_credits' && onTopUp && (
          <Button size="sm" className="mt-4 w-full" onClick={onTopUp}>
            <Coins className="mr-1.5 h-4 w-4" /> Top up and continue
          </Button>
        )}
        <p className="text-muted-foreground mt-3 text-[11px]">
          {connected === 'ws'
            ? 'Live'
            : connected === 'poll'
              ? 'Updating every few seconds'
              : 'Connecting…'}
        </p>
      </div>

      {/* Feed */}
      <div className="bg-card flex min-h-[420px] flex-col rounded-xl border">
        <div className="flex items-center justify-between border-b px-5 py-3">
          <p className="font-semibold">What's happening</p>
          <label className="text-muted-foreground flex items-center gap-1.5 text-xs">
            <input
              type="checkbox"
              checked={autoScroll}
              onChange={(e) => setAutoScroll(e.target.checked)}
            />{' '}
            Follow
          </label>
        </div>
        <div
          ref={feedRef}
          className="flex-1 space-y-2 overflow-y-auto px-5 py-4 text-sm"
          style={{ maxHeight: 520 }}
        >
          {feed.length === 0 && (
            <p className="text-muted-foreground flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Getting started…
            </p>
          )}
          {feed.map(({ e, d }) => {
            const Icon = d.icon;
            const tone =
              d.tone === 'ok'
                ? 'text-emerald-600'
                : d.tone === 'bad'
                  ? 'text-red-600'
                  : d.tone === 'muted'
                    ? 'text-muted-foreground'
                    : '';
            return (
              <div key={e.seq} className={`flex items-start gap-2 ${tone}`}>
                <Icon className="mt-0.5 h-4 w-4 shrink-0" />
                <p className={d.tone === 'normal' ? 'leading-relaxed' : 'text-xs leading-relaxed'}>
                  {d.text}
                </p>
              </div>
            );
          })}
          {run?.summary && TERMINAL.has(phase) && (
            <div className="bg-muted/50 mt-4 rounded-lg p-3">
              <p className="text-xs font-medium uppercase tracking-wide">Summary</p>
              <p className="mt-1 whitespace-pre-line leading-relaxed">{run.summary}</p>
            </div>
          )}
          {run?.error && (phase === 'failed' || phase === 'needs_credits') && (
            <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-red-800">
              <p className="text-sm">{run.error.message}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
